import { useState } from "react";
import ProductShippingStatusPopper from "./ProductShippingStatusPopper";
import { useTranslation } from "react-i18next";

interface Props {
  statusNumber: number;
  statusText: string;
  productIndicator?: string;
  prescriptionRequired?: boolean;
  disablePopper?: boolean;
  statusInformation?: string;
}

const stripHtml = (html: string): string =>
  html.replace(/<(?!img\b)[^>]+>/gi, "").trim();

export default function ProductShippingStatus({
                                                statusNumber,
                                                statusText,
                                                productIndicator,
                                                prescriptionRequired,
                                                disablePopper,
                                                statusInformation,
                                              }: Props) {
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const togglePopup = () => setIsPopupOpen((prev) => !prev);

  // Determine label text and handle empty statusText
  let label = stripHtml(statusText);
  if (!label) {
    if (statusNumber === 1 || statusNumber === 6) {
      label = t("search.hits.productCard.available");
    } else if (statusNumber === 2) {
      label = t("search.hits.productCard.inStock");
    } else if (
      productIndicator === "Z" &&
      (statusNumber === 0 || statusNumber === 10)
    ) {
      label = t("search.hits.productCard.notInAssortment");
    } else {
      label = t("search.hits.productCard.notAvailable");
    }
  }

  // Compute visual state
  const isNotInAssortment =
    productIndicator === "Z" && (statusNumber === 0 || statusNumber === 10);
  const isUnavailable =
    !isNotInAssortment &&
    statusNumber !== 1 &&
    statusNumber !== 2 &&
    statusNumber !== 6;
  const isAvailable = !isNotInAssortment && !isUnavailable;

  type StatusState = "available" | "notInAssortment" | "unavailable" | "other";
  const statusState: StatusState = isNotInAssortment
    ? "notInAssortment"
    : isUnavailable
      ? "unavailable"
      : isAvailable
        ? "available"
        : "other";

  const icon =
    statusState === "available"
      ? "#icon_circle-check"
      : statusState === "unavailable"
        ? "#icon_circle-x"
        : "#icon_circle-info";

  const availabilityClass =
    statusState === "available"
      ? " availability--available"
      : statusState === "unavailable"
        ? " availability--not-available"
        : " availability--not-in-sortiment";


  // Pre-built inner markup
  const availabilityMarkup = (
    <div className="availability-wrap">
      <div className={"availability" + availabilityClass} role="status">
        <svg className="icon" width="16" height="16" aria-hidden="true">
          <use href={icon}/>
        </svg>
        <span
          aria-hidden="true"
          dangerouslySetInnerHTML={{__html: label}}
        />
      </div>
    </div>
  );


  return (
    <div className="product-card__availability-container">
      {disablePopper ? (
        <div className="product-card__availability">{availabilityMarkup}</div>
      ) : (
        <button
          type="button"
          className="product-card__availability"
          aria-label={label}
          aria-expanded={isPopupOpen}
          onClick={togglePopup}
        >
          {availabilityMarkup}
        </button>
      )}

      {!disablePopper && isPopupOpen && (
        <ProductShippingStatusPopper
          statusNumber={statusNumber}
          statusText={statusText}
          productIndicator={productIndicator}
          prescriptionRequired={prescriptionRequired}
          statusInformation={statusInformation}
          onClose={togglePopup}
        />
      )}
    </div>
  );
}